import { Button, Group } from '@mantine/core';
import { IconPencil, IconPlus, IconStairs, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { RowActions } from 'src/Components/Shared/RowActions';
import { trackBedPanelOpened } from 'src/Services/Mixpanel/events';
import { RoomDto } from 'src/types/cartography.types';

import useBuildingContent from '../../hooks/useBuildingContent';
import { isSleepingRoomType } from '../../referential';
import { BuildingRoof } from '../BuildingRoof';
import { FloorBand } from '../FloorBand';

import styles from './BuildingHouse.module.scss';
import { Props } from './BuildingHouse.types';

const BuildingHouse = ({
  building,
  summary,
  onOpenRoom,
  onEditBuilding,
  onDeleteBuilding,
}: Props) => {
  const { t } = useTranslation();

  const handleRoomCreated = (created: RoomDto) => {
    if (!isSleepingRoomType(created.type)) return;
    trackBedPanelOpened({ source: 'after_room_create' });
    onOpenRoom(created.id, { openBedForm: true });
  };

  const {
    hasFloors,
    groups,
    enSuiteLabelOf,
    openCreateFloor,
    openEditFloor,
    openCreateRoom,
    handleEditRoom,
    handleToggleOutOfService,
    handleDeleteRoom,
    handleDeleteFloor,
    drawers,
  } = useBuildingContent(building, 'desc', handleRoomCreated);

  const typeLabel = building.type
    ? t(`admin.structure.buildingTypes.${building.type}`, building.type)
    : null;

  const hasContent = building.rooms.length > 0 || building.floors.length > 0;

  return (
    <div className={styles.house}>
      <BuildingRoof type={building.type} />
      <div className={styles.body}>
        <div className={styles.summary}>
          <div>
            <h3 className={styles.title}>
              {building.name}
              {typeLabel && <span className={styles.typeChip}>· {typeLabel}</span>}
            </h3>
            <div className={styles.subline}>{summary}</div>
          </div>
          <div className={styles.actions}>
            <RowActions
              actions={[
                {
                  key: 'edit',
                  label: t('common.edit', 'Modifier'),
                  icon: <IconPencil size={18} />,
                  onClick: () => onEditBuilding(building),
                },
                {
                  key: 'delete',
                  label: t('common.delete', 'Supprimer'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  onClick: () => onDeleteBuilding(building),
                },
              ]}
            />
          </div>
        </div>

        {hasContent ? (
          <>
            <Group gap="xs" className={styles.addFloor}>
              <Button
                variant="primary"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => openCreateRoom(null)}
              >
                {t('admin.structure.rooms.add', 'Ajouter une pièce')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconStairs size={16} />}
                onClick={openCreateFloor}
              >
                {t('admin.structure.floors.add', 'Ajouter un niveau')}
              </Button>
            </Group>
            <div className={styles.floors}>
              {groups.map(group => (
                <FloorBand
                  key={group.floor?.id ?? 'no-floor'}
                  floor={group.floor}
                  rooms={group.rooms}
                  showHeader={hasFloors}
                  onAddRoom={openCreateRoom}
                  onEditFloor={openEditFloor}
                  onDeleteFloor={handleDeleteFloor}
                  onOpenRoom={onOpenRoom}
                  onEditRoom={handleEditRoom}
                  onToggleOutOfService={handleToggleOutOfService}
                  onDeleteRoom={handleDeleteRoom}
                  enSuiteLabelOf={enSuiteLabelOf}
                />
              ))}
            </div>
          </>
        ) : (
          <div className={styles.empty}>
            {t('admin.structure.rooms.empty', 'Aucune pièce dans ce bâtiment')}
            <Group gap="xs" className={styles.addFloor}>
              <Button
                variant="primary"
                size="sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => openCreateRoom(null)}
              >
                {t('admin.structure.rooms.add', 'Ajouter une pièce')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconStairs size={16} />}
                onClick={openCreateFloor}
              >
                {t('admin.structure.floors.add', 'Ajouter un niveau')}
              </Button>
            </Group>
          </div>
        )}
      </div>

      {drawers}
    </div>
  );
};

export default BuildingHouse;
